interface HeroSectionProps {
  onInspectionClick: () => void;
  onLearnClick: () => void;
}

export default function HeroSection({ onInspectionClick, onLearnClick }: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#0a0e1a]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/55ee081c2f6935dcbecd1dee8e0a5933.jpg"
          alt="Concrete with water infiltration"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a]/60 via-[#0a0e1a]/50 to-[#0a0e1a]"></div>
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-16 md:pb-20 flex flex-col items-center text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#1a3a5c]/30 border border-[#4a7fa5]/40 text-[#8ab4cc] text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full mb-6 md:mb-8">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-fill text-[#4a7fa5]"></i>
          </span>
          <span className="hidden sm:inline">The Concrete Leak Specialist — CLWizard®</span>
          <span className="sm:hidden">CLWizard® — Concrete Leak Specialist</span>
        </div>

        {/* Headline */}
        <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5 md:mb-6 max-w-4xl">
          Your Building Has Been Repaired Before.{" "}
          <span className="text-[#4a7fa5]">The Leak Came Back.</span>{" "}
          Here Is Why.
        </h1>

        {/* Sub */}
        <p className="text-base md:text-xl text-gray-300 max-w-2xl mb-8 md:mb-10 leading-relaxed">
          The problem was never at the surface. It was always{" "}
          <strong className="text-white">inside the concrete</strong> — and no surface treatment can reach it.
        </p>

        {/* Trust strip — 2x2 on mobile, 4 cols on md+ */}
        <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-3 md:gap-8 mb-8 md:mb-10 w-full max-w-sm md:max-w-none">
          {[
            { icon: "ri-forbid-2-line", label: "No Demolition" },
            { icon: "ri-time-line", label: "No Downtime" },
            { icon: "ri-drop-fill", label: "Active Leaks" },
            { icon: "ri-shield-star-fill", label: "5-Year Warranty" },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-center gap-2 text-white text-sm font-semibold">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-[#4a7fa5] text-base`}></i>
              </span>
              {item.label}
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 w-full max-w-md md:max-w-none">
          <button
            onClick={onInspectionClick}
            className="bg-[#1a3a5c] hover:bg-[#0f2a45] text-white font-bold px-6 md:px-8 py-4 rounded-md transition-all text-sm md:text-base w-full sm:w-auto shadow-lg shadow-[#1a3a5c]/40"
          >
            Schedule a Free Engineering Inspection
          </button>
          <button
            onClick={onLearnClick}
            className="border-2 border-white/40 hover:border-white text-white font-bold px-6 md:px-8 py-4 rounded-md transition-all text-sm md:text-base w-full sm:w-auto bg-white/5 hover:bg-white/10"
          >
            Understand Why Your Repair Failed
          </button>
        </div>

        {/* Disclaimer */}
        <p className="text-gray-400 text-xs tracking-widest uppercase font-semibold">
          No Obligation. &mdash; No Guesswork. &mdash; Just Technical Clarity.
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-gray-500 animate-bounce">
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-lg"></i>
        </span>
      </div>
    </section>
  );
}
